import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import React, { useRef, useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { ATT_ENV, RUNNING } from '../../../Constants/constants';
import {
  GET_ALL_URLS,
  GET_IMPLEMENTED_COMPONENTS,
} from '../../../GraphQL/ManageUrls/TRFX/Queries';
import { EDIT_TEST_EXECUTION } from '../../../GraphQL/RunTests/aTC/Mutations';
import { GET_EDITED_URLS } from '../../../GraphQL/RunTests/TRFX/Queries';
import { GET_TEST_EXECUTIONS_BY_ID } from '../../../GraphQL/TestExecutions/TRFX/Queries';
import { TEST_EXECUTION_EXECUTE_TESTS } from '../../../GraphQL/TestResults/TRFX/Mutations';
import TRFXRunTests from '../RunTests';
import { AppContext } from '../../UI/contexts';
import { DUPLICATE_TEST_EXECUTION } from '../../../GraphQL/RunTests/TRFX/Mutations';
import { setData } from '../../../App/Slices/RunTests';
import { useDispatch } from 'react-redux';

function RunTests() {
  const { testExecutionId } = useParams();

  const { user } = useContext(AppContext);

  const components = useRef([]);
  const dispatch = useDispatch();
  const [env, setEnv] = useState('dev');
  const [name, setName] = useState(null);
  const [description, setDescription] = useState(null);
  const [smokeTest, setSmokeTest] = useState(null);
  const [filtersDefaultValues, setFiltersDefaultValues] = useState(false);
  const [executedUrls, setExecutedUrls] = useState([]);
  const [region, setRegion] = useState();
  const [isEditMode, setIsEditMode] = useState(false);
  const [includeTracking, setIncludeTracking] = useState(false);
  const [executionType, setExecutionType] = useState('standard');

  const { loading } = useQuery(GET_ALL_URLS, {
    variables: { env, limit: 10000 },
    onCompleted: (data) => {
      dispatch(setData(data));
    },
    fetchPolicy: 'cache-first',
  });

  const [testById, { loading: loadingEditedExecution }] = useLazyQuery(
    GET_TEST_EXECUTIONS_BY_ID,
    {
      onCompleted: (data) => {
        let test = data.testsExecutionFindById;
        setName(test.name);
        setDescription(test.description);
        setSmokeTest(test.smokeTest);
        setEnv(test.env);
        setRegion(test.region);
        setIncludeTracking(test.includeTrackingValidations);
        setExecutionType(test.executionType);
        if (test.filters) setFiltersDefaultValues(test.filters);
      },
    }
  );

  const [editedUrls, { loading: loadingEditedExecutionUrls }] = useLazyQuery(
    GET_EDITED_URLS,
    {
      onCompleted: (data) => {
        setExecutedUrls(
          data.urlTestsFindMany.map((u) => {
            return { url: u.url, app: u.context.application };
          })
        );
      },
    }
  );

  useEffect(() => {
    if (!testExecutionId) return;
    if (window.location.pathname.includes('edit')) setIsEditMode(true);
    testById({ variables: { id: testExecutionId } });
    editedUrls({ variables: { teId: testExecutionId, limit: 10000 } });
  }, []);

  useQuery(GET_IMPLEMENTED_COMPONENTS, {
    fetchPolicy: 'cache-first',
    onCompleted: (d) => {
      components.current = (d.implementedWCFindMany || []).map((d) => d.name);
    },
  });

  const [runTests, { loading: loadingRunTest }] = useMutation(
    TEST_EXECUTION_EXECUTE_TESTS,
    {
      onCompleted: (data) => {
        if (data.testsExecutionExecuteTest.status !== RUNNING) {
          window.flash(
            `Your execution was placed in a queue for processing`,
            'info'
          );
        }
      },
      onError: (error) => {
        const message =
          error.message || 'ERROR IN MUTATION TestExecutionExecuteTests';
        window.flash(message, 'error');
      },
    }
  );

  const [editExecution, { loading: loadingEditMutation }] = useMutation(
    EDIT_TEST_EXECUTION,
    {
      onCompleted: (data) => {
        window.flash(`Successfully edited test execution`, 'success');
      },
      onError: (error) => {
        const message = error.message || 'ERROR IN MUTATION EditTestExecution';
        window.flash(message, 'error');
      },
    }
  );

  const [duplicateExecution, { loading: loadingDuplicateMutation }] =
    useMutation(DUPLICATE_TEST_EXECUTION, {
      onCompleted: (data) => {
        window.flash(`Successfully duplicated test execution`, 'success');
      },
      onError: (error) => {
        const message = error.message || 'ERROR IN MUTATION TestExecutionCopy';
        window.flash(message, 'error');
      },
    });
  // return variables object to use in run tests mutation
  const getRunTestVariables = (testConfig, selectedUrls, filters) => {
    const filtersList = filters.map(({ name, value }) => ({ name, value }));
    const userId = user._id || '61097bfa97ff9f0009fe3954';
    const componentsFilter = filtersList[filtersList.length - 1];
    const isComponentsFilterEmpty =
      !componentsFilter.value || componentsFilter.value.length === 0;

    const contexts = selectedUrls.map((su) => {
      // If components filter is empty, use all available components
      // Otherwise, filter components based on the filter selection
      const filteredComponents = isComponentsFilterEmpty
        ? su.components
        : su.components.filter((c) => componentsFilter.value.includes(c));

      return {
        ...su,
        components: filteredComponents,
      };
    });
    return {
      contexts,
      name: testConfig.name,
      description: testConfig.description,
      attEnv: ATT_ENV,
      smokeTest: testConfig.smokeTest,
      filters: filtersList,
      region: testConfig.region,
      userId,
      includeTrackingValidations: testConfig.includeTracking,
      executionType: testConfig.executionType || 'standard',
    };
  };
  const getEditExecutionVariables = (
    teId,
    testConfig,
    selectedUrls,
    filters
  ) => {
    const filtersList = filters.map(({ name, value }) => ({ name, value }));
    const contexts = selectedUrls.map((su) => {
      const filteredComponents = su.components.filter((c) =>
        filtersList[filtersList.length - 1].value.includes(c)
      );
      return {
        ...su,
        components: filteredComponents,
      };
    });
    return {
      teId,
      contexts,
      name: testConfig.name,
      description: testConfig.description,
      env: testConfig.env,
      filters: filtersList,
      smokeTest: testConfig.smokeTest,
      region: testConfig.region,
      includeTrackingValidations: testConfig.includeTracking,
    };
  };

  return (
    <TRFXRunTests
      implementedComponents={components.current}
      loading={
        loading ||
        loadingRunTest ||
        loadingEditMutation ||
        loadingEditedExecution ||
        loadingEditedExecutionUrls ||
        loadingDuplicateMutation
      }
      env={env}
      setEnv={setEnv}
      runTests={runTests}
      getRunTestsVariables={getRunTestVariables}
      redirectUrl='/trfx-test-executions'
      prevName={name}
      prevDescription={description}
      wasSmokeTest={smokeTest}
      filtersDefaultValues={filtersDefaultValues}
      executedUrls={executedUrls}
      region={region}
      isEditMode={isEditMode}
      editExecution={editExecution}
      getEditExecutionVariables={getEditExecutionVariables}
      duplicateExecution={duplicateExecution}
      includedTrackingValidations={includeTracking}
      executionType={executionType}
    />
  );
}
export default RunTests;
